import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { opApi } from "@/lib/api";
import { CodeBlock } from "@/components/mc/InstallSnippet";

const ORIGIN = window.location.origin;

function Step({ title, hint, code, testid }) {
  return (
    <div className="space-y-2" data-testid={`${testid}-section`}>
      <h3 className="font-display font-bold text-base">{title}</h3>
      <p className="text-sm text-muted-foreground">{hint}</p>
      <CodeBlock code={code} testid={testid} />
    </div>
  );
}

// MorrConnect "Add to your website": one script + drop-in pieces that copy the site's own look.
export function ConnectInstall({ slug }) {
  const [pro, setPro] = useState("your-professional");
  useEffect(() => { opApi.get("/tenant/connect/profiles").then(({ data }) => data[0]?.slug && setPro(data[0].slug)).catch(() => {}); }, []);

  const script = `<!-- Paste once in your site's <head> or "Custom Code / Footer" area. -->
<script async src="${ORIGIN}/connect.js"
  data-business="${slug}"
  data-floating-button="Talk to an agent"
  data-side-tab="Contact an agent"></script>`;

  return (
    <div className="space-y-8" data-testid="connect-install">
      <div className="flex flex-wrap items-center gap-3 text-sm bg-secondary p-3">
        <span className="flex-1 min-w-[240px]">Everything you add uses your website's own fonts, colors, buttons and form style, so it looks like part of your site. Professionals open in a pop-up, so visitors never leave your site.</span>
        <a href={`/connect-demo.html?business=${encodeURIComponent(slug)}&pro=${encodeURIComponent(pro)}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 underline font-semibold" data-testid="connect-install-demo-link">
          See it on a sample website <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <Step testid="cn-snippet-script" title="1. Add the MorrConnect script"
        hint="Required once per site. The two optional lines add a floating 'Talk to an agent' button (opens a contact form) and a 'Contact an agent' tab on the right middle of the screen (opens your team's cards). Change the words, or delete a line to turn it off."
        code={script} />
      <Step testid="cn-snippet-directory" title="2. Team directory"
        hint="Search, filters and professional cards. Clicking a card opens the full profile in a pop-up."
        code={`<div data-morrconnect-directory></div>`} />
      <Step testid="cn-snippet-card" title="3. Single professional card"
        hint="One person's card with Call, Email, Book a call, Save contact and View profile. Use their profile link name."
        code={`<div data-morrconnect-card="${pro}"></div>`} />
      <Step testid="cn-snippet-form" title="4. Contact / lead form"
        hint="Leads go to the best-matching professional. Add a profile link name to send every message to one person."
        code={`<!-- Routed to the best match -->
<div data-morrconnect-form></div>

<!-- Always to one professional -->
<div data-morrconnect-form="${pro}"></div>`} />
      <Step testid="cn-snippet-book" title="5. Book a call button"
        hint="Turns any button or link on your site into one that opens the booking calendar in a pop-up."
        code={`<button data-morrconnect-book>Book a call</button>

<!-- With one professional -->
<a href="#" data-morrconnect-book="${pro}">Book with me</a>`} />
      <Step testid="cn-snippet-open" title="6. Your own buttons for the contact form or team"
        hint="Use your existing buttons to open the contact form or the team side panel."
        code={`<button data-morrconnect-open="contact">Contact us</button>
<button data-morrconnect-open="team">Meet the team</button>`} />
      <Step testid="cn-snippet-style" title="Prefer the MorrConnect look?"
        hint="Add this to the script tag to use your brand color with a clean look instead of copying your website."
        code={`data-style="brand"`} />
    </div>
  );
}
